import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concat,
  combineLatest,
  concatMap,
  delay,
  from,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  toArray,
  withLatestFrom,
} from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { AppState } from 'src/app/core/store/state';
import { DialogsActions } from '../../dialogs/store/actions';
import { NotificationsActions } from '../../notifications/store/actions';
import {
  WorkspaceCreateMetadata,
  WorkspaceCreateRequestMetadata,
  WorkspacesApiService,
} from '../services/workspaces-api.service';
import { ClauseVarianceAnalysisActions, GetReviewActions, WorkspacesActions } from './actions';
import {
  sendWorkspaceAddDocumentsSuccessNotification,
  sendWorkspaceCreationSuccessNotification,
  sendWorkspaceDocumentReviewFailedNotification,
} from 'src/app/core/utils/workspaces.utils';
import {
  DocumentProcessingApiService,
  DocumentProcessingClause,
  DocumentProcessingPagedReviewResponse,
} from '../../documents/services/document-processing-api.service';
import {
  FileUploadApiService,
  FileUploadResultDto,
} from 'src/app/shared/services/file-upload.api-service';
import { isPresent } from 'src/app/core/utils/isPresent';
import { selectUser } from 'src/app/core/store/selectors';
import {
  selectCurrentReviewPageByDocumentId,
  selectNonReferenceDocuments,
  selectReviewDoneWithErrors,
  selectTotalReviewPagesByDocumentId,
  selectWorkspaceDetails,
} from './selectors';
import { VarianceAnalysisApiService } from '../services/variance-analysis-api.service';

const workspaceBatchUploadLimitBytes = 50 * 1024 * 1024;
const workspaceFileValidationErrorMessage = 'File validation failed';

interface WorkspaceUploadEntry {
  requestId: string;
  metadata: WorkspaceCreateMetadata;
  file: File;
}

class WorkspaceUploadError extends Error {
  constructor(
    public readonly translationKey: string,
    public readonly validationErrors: string[] = [],
  ) {
    super(translationKey);
    this.name = 'WorkspaceUploadError';
  }
}

@Injectable()
export class FeatureEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private workspacesApiService: WorkspacesApiService,
    private fileUploadApiService: FileUploadApiService,
    private varianceAnalysisApiService: VarianceAnalysisApiService,
    private documentProcessingApiService: DocumentProcessingApiService,
    private dialogRef: MatDialog,
    private translocoService: TranslocoService,
  ) {}

  getWorkspaces$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(WorkspacesActions.getWorkspaces))]).pipe(
      map(() => {
        return WorkspacesActions.loadWorkspaces();
      }),
    ),
  );

  loadWorkspaces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.loadWorkspaces),
      switchMap(() => {
        return this.workspacesApiService.getWorkspaces$().pipe(
          map((workspaces) => WorkspacesActions.setWorkspaces({ workspaces })),
          catchError((response) => {
            return of(WorkspacesActions.loadWorkspacesFail());
          }),
        );
      }),
    ),
  );

  createWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.createWorkspace),
      mergeMap((props) =>
        this.uploadWorkspaceFiles$(props.metadata, props.filesById)
          .pipe(
            switchMap((metadata) => this.workspacesApiService.createWorkspace$(props.name, metadata)),
            map((workspace) => WorkspacesActions.createWorkspaceSuccess({ workspace })),
            catchError((response) => {
              return of(
                WorkspacesActions.createWorkspaceFail(),
                this.createWorkspaceErrorDialog(response, 'errors.createWorkspace'),
              );
            }),
          ),
      ),
    ),
  );

  createWorkspaceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.createWorkspaceSuccess),
      map((props) => {
        this.store.dispatch(WorkspacesActions.closeCreatingWorkspaceModal());
        sendWorkspaceCreationSuccessNotification(
          this.store,
          this.translocoService,
          props.workspace,
        );

        this.dialogRef.closeAll();
        return WorkspacesActions.getWorkspaces();
      }),
    ),
  );

  createWorkspaceCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.createWorkspaceCancel),
      map((props) => {
        return WorkspacesActions.createWorkspaceCancelled();
      }),
    ),
  );

  private uploadWorkspaceFiles$(
    metadataByRequestId: Record<string, WorkspaceCreateMetadata>,
    filesByRequestId: Record<string, File>,
  ) {
    const uploadEntries = this.getWorkspaceUploadEntries(metadataByRequestId, filesByRequestId);

    if (!uploadEntries.length) {
      return of([] as WorkspaceCreateRequestMetadata[]);
    }

    const totalFileSize = uploadEntries.reduce((total, entry) => total + entry.file.size, 0);

    return totalFileSize <= workspaceBatchUploadLimitBytes
      ? this.uploadWorkspaceFilesBatch$(uploadEntries)
      : this.uploadWorkspaceFilesInParallel$(uploadEntries);
  }

  private uploadWorkspaceFilesBatch$(uploadEntries: WorkspaceUploadEntry[]) {
    return this.fileUploadApiService.uploadFiles$(uploadEntries.map(({ file }) => file)).pipe(
      map((uploadResponse) => {
        const uploadResultsByKey = uploadResponse.results.reduce(
          (accumulator, result) => {
            const key = this.getUploadResultKey(result);
            accumulator[key] = [...(accumulator[key] || []), result];
            return accumulator;
          },
          {} as Record<string, FileUploadResultDto[]>,
        );

        return uploadEntries.map((entry) => {
          const key = this.getWorkspaceFileKey(entry.file);
          const uploadedFile = uploadResultsByKey[key]?.shift();

          return this.mapWorkspaceUploadEntry(entry, uploadedFile);
        });
      }),
    );
  }

  private uploadWorkspaceFilesInParallel$(uploadEntries: WorkspaceUploadEntry[]) {
    return from(uploadEntries).pipe(
      mergeMap((entry) =>
        this.fileUploadApiService.uploadFiles$([entry.file]).pipe(
          map((uploadResponse) => {
            const uploadedFile = uploadResponse.results?.[0];

            return this.mapWorkspaceUploadEntry(entry, uploadedFile);
          }),
        ),
      ),
      toArray(),
    );
  }

  private getWorkspaceUploadEntries(
    metadataByRequestId: Record<string, WorkspaceCreateMetadata>,
    filesByRequestId: Record<string, File>,
  ): WorkspaceUploadEntry[] {
    return Object.entries(metadataByRequestId).map(([requestId, metadata]) => {
      const file = filesByRequestId[requestId];

      if (!file) {
        throw new Error(`Missing file for workspace request: ${requestId}`);
      }

      return {
        requestId,
        metadata,
        file,
      };
    });
  }

  private mapWorkspaceUploadEntry(
    entry: WorkspaceUploadEntry,
    uploadedFile: FileUploadResultDto | undefined,
  ): WorkspaceCreateRequestMetadata {
    if (
      uploadedFile?.errorMessage === workspaceFileValidationErrorMessage ||
      !!uploadedFile?.validationErrors?.length
    ) {
      console.log(uploadedFile.validationErrors);

      throw new WorkspaceUploadError(
        'errors.fileValidationFailed',
        uploadedFile.validationErrors,
      );
    }

    if (!uploadedFile?.isSuccess || !uploadedFile.fileId) {
      throw new Error(`Workspace file upload failed: ${entry.requestId}`);
    }

    return {
      fileId: uploadedFile.fileId,
      documentTypeId: entry.metadata.documentTypeId,
      languageId: entry.metadata.languageId,
      positionId: entry.metadata.positionId,
    };
  }

  private getWorkspaceFileKey(file: File): string {
    return `${file.name}::${file.size}`;
  }

  private getUploadResultKey(result: FileUploadResultDto): string {
    return `${result.fileName}::${result.fileSize}`;
  }

  private isWorkspaceUploadError(error: unknown): error is WorkspaceUploadError {
    return error instanceof WorkspaceUploadError;
  }

  private createWorkspaceErrorDialog(error: unknown, fallbackTranslationKey: string) {
    const title = this.isWorkspaceUploadError(error)
      ? this.translocoService.translate(error.translationKey)
      : this.translocoService.translate(fallbackTranslationKey);

    return DialogsActions.showDialog({
      data: {
        actionsKeys: ['close'],
        content: '',
        title,
      },
    });
  }

  addWorkspaceDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.addWorkspaceDocuments),
      mergeMap((props) =>
        this.uploadWorkspaceFiles$(props.model.metadata, props.model.filesById)
          .pipe(
            switchMap((metadata) =>
              this.workspacesApiService.addDocumentToWorkspace$(props.model.id, metadata),
            ),
            map((workspace) => WorkspacesActions.addWorkspaceDocumentsSuccess({ workspace })),
            catchError((response) => {
              if (this.isWorkspaceUploadError(response)) {
                return of(
                  WorkspacesActions.addWorkspaceDocumentsFail(),
                  this.createWorkspaceErrorDialog(response, 'errors.createWorkspace'),
                );
              }

              return of(WorkspacesActions.addWorkspaceDocumentsFail());
            }),
          ),
      ),
    ),
  );

  addWorkspaceDocumentsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.addWorkspaceDocumentsSuccess),
      map((props) => {
        this.store.dispatch(WorkspacesActions.closeAddWorkspaceDocumentsModal());
        sendWorkspaceAddDocumentsSuccessNotification(
          this.store,
          this.translocoService,
          props.workspace,
        );

        this.dialogRef.closeAll();
        return WorkspacesActions.getWorkspaceDetails({ id: props.workspace.id });
      }),
    ),
  );

  addWorkspaceDocumentsCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.addWorkspaceDocumentsCancel),
      map((props) => {
        return WorkspacesActions.addWorkspaceDocumentsCancelled();
      }),
    ),
  );

  deleteWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.deleteWorkspace),
      map((props) => {
        return DialogsActions.showDialogCheckbox({
          data: {
            actionsKeys: ['yes', 'no'],
            content: '',
            title: this.translocoService.translate('modalBody.confirmDeleteWorkspace'),
            checkboxModel: true, // Permanently delete all documents belonging to this workspace
            checkboxText: this.translocoService.translate('modalBody.confirmDeleteAllDocuments'),
          },
          callback: (result) => {
            if (result?.action === 'yes') {
              return this.store.dispatch(
                WorkspacesActions.deleteWorkspaceConfirmed({
                  id: props.id,
                  keepDocuments: !result.checkboxModel,
                }),
              );
            } else {
              return of(
                DialogsActions.setRef({ ref: null }),
                WorkspacesActions.deleteWorkspaceCancel(),
              );
            }
          },
        });
      }),
    ),
  );

  deleteWorkspaceConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.deleteWorkspaceConfirmed),
      mergeMap((props) => {
        return this.workspacesApiService
          .deleteWorkspace$(props.id, props.keepDocuments ?? true)
          .pipe(
            map(() => WorkspacesActions.deleteWorkspaceSuccess()),
            catchError((response) => {
              return of(
                DialogsActions.setRef({ ref: null }),
                WorkspacesActions.deleteWorkspaceFail(),
              );
            }),
          );
      }),
    ),
  );

  deleteWorkspaceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.deleteWorkspaceSuccess),
      map(() => {
        return WorkspacesActions.getWorkspaces();
      }),
    ),
  );

  renameWorkspaceCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.renameWorkspaceCancel),
      map((props) => {
        return WorkspacesActions.renameWorkspaceCancelled();
      }),
    ),
  );

  renameWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.renameWorkspace),
      mergeMap((props) =>
        this.workspacesApiService.renameWorkspace$(props.name, props.id).pipe(
          map((workspace) => WorkspacesActions.renameWorkspaceSuccess({ workspace })),
          catchError((response) => {
            return of(WorkspacesActions.renameWorkspaceFail());
          }),
        ),
      ),
    ),
  );

  renameWorkspaceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.renameWorkspaceSuccess),
      map((props) => {
        this.store.dispatch(WorkspacesActions.closeRenamingWorkspaceModal());
        this.store.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.greenTick,
            message: this.translocoService.translate('text.workspaceUpdated', {
              name: props.workspace.name,
            }),
          }),
        );

        this.dialogRef.closeAll();
        return WorkspacesActions.getWorkspaces();
      }),
    ),
  );

  getWorkspaceDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacesActions.getWorkspaceDetails),
      mergeMap((props) =>
        this.workspacesApiService.getWorkspaceDetails$(props.id).pipe(
          map((workspaceDetails) => {
            return WorkspacesActions.setWorkspaceDetails({ workspaceDetails });
          }),
          // catchError((response) => {
          //   return of(WorkspacesActions.getWorkspaceDetailsFail());
          // }),
        ),
      ),
    ),
  );

  analyzeClauseVariance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseVarianceAnalysisActions.analyzeClauseVariance),
      mergeMap((props) =>
        this.varianceAnalysisApiService
          .analyzeClauseVariance$(props.workspaceId, props.referenceDocumentId)
          .pipe(
            map((response) =>
              ClauseVarianceAnalysisActions.analyzeClauseVarianceSuccess({ response }),
            ),
            catchError((response) => {
              return of(ClauseVarianceAnalysisActions.analyzeClauseVarianceFail());
            }),
          ),
      ),
    ),
  );

  analyzeClauseVarianceSuccessStartReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseVarianceAnalysisActions.analyzeClauseVarianceSuccess),
      withLatestFrom(this.store.select(selectNonReferenceDocuments)),
      mergeMap(([_, nonReferenceDocuments]) => {
        const documentIds = nonReferenceDocuments?.map((document) => document.id) ?? [];
        if (!documentIds.length) {
          return of(GetReviewActions.initializeReviewDataForDocuments({ documentIds: [] }));
        }

        return concat(
          of(GetReviewActions.initializeReviewDataForDocuments({ documentIds })),
          from(documentIds).pipe(
            concatMap((documentId, index) =>
              of(
                GetReviewActions.getPagedReviewData({
                  documentId,
                  playbookId: _.response.playbook.uniqueId,
                }),
              ).pipe(delay(index * constants.delayBetweenGetPagedReviewData)),
            ),
          ),
        );
      }),
    ),
  );

  getReviewDataByPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetReviewActions.getPagedReviewData),
      mergeMap((props) =>
        this.store.select(selectCurrentReviewPageByDocumentId(props.documentId)).pipe(
          take(1),
          withLatestFrom(
            this.store.select(selectUser).pipe(take(1)),
            this.store.select(selectTotalReviewPagesByDocumentId(props.documentId)).pipe(take(1)),
            this.store.select(selectReviewDoneWithErrors).pipe(take(1)),
            this.store.select(selectNonReferenceDocuments).pipe(take(1)),
            this.store.select(selectWorkspaceDetails).pipe(take(1), filter(isPresent)),
          ),
          switchMap(
            ([
              pageNumber,
              user,
              totalReviewPages,
              isReviewDoneWithErrors,
              nonReferenceDocuments,
              workspace,
            ]) => {
              const documentIndex =
                nonReferenceDocuments?.findIndex((document) => document.id === props.documentId) ??
                -1;
              const callOffsetMs =
                Math.max(documentIndex, 0) * constants.delayBetweenGetPagedReviewData;
              const nextCallDelayMs = constants.delayBetweenGetPagedReviewData + callOffsetMs;

              this.store.dispatch(
                GetReviewActions.setDocumentReviewDoneWithErrors({ value: false }),
              );
              //checkAndSendReviewStartedNotification(pageNumber, this.store);

              return this.documentProcessingApiService
                .pageReview$(props.documentId, pageNumber, props.playbookId)
                .pipe(
                  mergeMap((pagedReviewData: DocumentProcessingPagedReviewResponse) => {
                    pagedReviewData.clauses.map((item: DocumentProcessingClause) => {
                      item.includeExplanatoryComment = user?.useDefaultComment ?? true;
                    });

                    //fetchReviewDataFromLocalStorage(guid, pagedReviewData);

                    //sendPageReviewNotification(pagedReviewData, this.store);
                    this.store.dispatch(
                      GetReviewActions.getPagedReviewDataSuccess({ pagedReviewData }),
                    );

                    if (
                      pageNumber !== pagedReviewData.totalPages &&
                      pagedReviewData.nextPageNumber !== null &&
                      pagedReviewData.totalPages !== 0
                    ) {
                      return of(
                        GetReviewActions.getPagedReviewData({
                          documentId: props.documentId,
                          playbookId: props.playbookId,
                        }),
                      ).pipe(delay(nextCallDelayMs));
                    } else {
                      if (isReviewDoneWithErrors) {
                        //sendCompleteWithErrorsReviewNotification(this.store);
                      } else {
                        // sendCompleteReviewNotification
                        console.log('sendCompleteReviewNotification(this.store)');
                      }

                      return of(
                        GetReviewActions.getPagedReviewDataComplete({
                          documentId: props.documentId,
                        }),
                      );
                    }
                  }),
                  catchError((response) => {
                    this.store.dispatch(
                      GetReviewActions.setDocumentReviewDoneWithErrors({ value: true }),
                    );

                    if (pageNumber !== totalReviewPages) {
                      sendWorkspaceDocumentReviewFailedNotification(
                        this.store,
                        this.translocoService,
                        props.documentId,
                        workspace,
                      );
                      setTimeout(() => {
                        this.store.dispatch(
                          GetReviewActions.setPageNumberOfReviewData({
                            documentId: props.documentId,
                            pageNumber: pageNumber + 1,
                          }),
                        );
                        this.store.dispatch(
                          GetReviewActions.getPagedReviewData({
                            documentId: props.documentId,
                            playbookId: props.playbookId,
                          }),
                        );
                      }, nextCallDelayMs);
                    } else {
                      sendWorkspaceDocumentReviewFailedNotification(
                        this.store,
                        this.translocoService,
                        props.documentId,
                        workspace,
                      );
                      //sendCompleteWithErrorsReviewNotification(this.store);
                      console.log('sendCompleteWithErrorsReviewNotification(this.store)');
                      this.store.dispatch(
                        GetReviewActions.getPagedReviewDataComplete({
                          documentId: props.documentId,
                        }),
                      ); // we have to done it in any cases
                    }

                    return of(
                      GetReviewActions.getPagedReviewDataFailure({ documentId: props.documentId }),
                    ); // use failed count as a param and savedata in store, clear when renew
                  }),
                );
            },
          ),
        ),
      ),
    ),
  );
}
